import { useMemo, useState } from "react"
import { shape, bool, number, array, func, arrayOf } from "prop-types"
import { navigate } from "@reach/router"
import Icon from "components/planning_center_icon"
import { BlankState, Table } from "components/ui"
import CapacityField from "./CapacityField"
import WaitlistField from "./WaitlistField"
import { pluralize, isBlank, isPresent } from "utils/helpers"
import { HelperShelfMessage, HelperShelfTrigger } from "components/helper_shelf"
import SelectionTypesOptionsWrapper from "./SelectionTypesOptionsWrapper"
import useOptionsUpdate from "./useOptionsUpdate"
import useStatus from "./useStatus"
import useFeature from "hooks/use_feature"
import useFlipperFlag from "hooks/use_flipper_flag"

const SelectionTypesList = ({
  onCapacitySave,
  onWaitlistSave,
  onDragEnd,
  signup,
  selectionTypes,
  signupTimes: signupTimesFromProps,
  isLoading
}) => {
  const [editCapacity, setEditCapacity] = useState(false)
  const [validation, setValidation] = useState({})
  const [totalCapacity, setTotalCapacity] = useState(signup?.maximumCapacity)
  const [attendeeTypeAttrs, setAttendeeTypeAttrs] = useState({})
  const [capacityChanges, setCapacityChanges] = useState({})
  const status = useStatus()
  const [selectedColumns, setSelectedColumns] = useState([])
  const [registerForSeparateTimesEnabled, setRegisterForSeparateTimesEnabled] = useState(
    signup?.registerForSeparateTimes
  )
  const [signupTimes, setSignupTimes] = useState(
    signupTimesFromProps.sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
  )

  const { enabled: unifiedSignupsEnabled } = useFeature("unified_detailed_signups_ccw", signup.id)
  const detailedWaitlistFlipperEnabled = useFlipperFlag("ROLLOUT_detailed_waitlist")

  const activeAttendees = useMemo(() => {
    let attendees = {}

    signupTimes.forEach(signupTime => {
      const key = `activeAttendeesFor${signupTime.id}`
      attendees[key] = {
        activeAttendeesCount: selectionTypes
          .filter(at =>
            registerForSeparateTimesEnabled
              ? at.signupTimeId == signupTime.id
              : at.signupTimeId == null
          )
          .reduce((total, at) => total + at.activeAttendeesCount, 0)
      }
      selectionTypes
        .filter(at => (registerForSeparateTimesEnabled ? at.signupTimeId == signupTime.id : at))
        .forEach(
          selection =>
            (attendees[key][selection.templateAttendeeTypeId ?? selection.id] =
              selection.activeAttendeesCount)
        )
    })

    if (!signupTimes.some(Boolean)) {
      const key = "activeAttendeesForDateless"
      attendees[key] = {
        activeAttendeesCount: selectionTypes
          .filter(at => at.signupTimeId == null)
          .reduce((total, at) => total + at.activeAttendeesCount, 0)
      }
      selectionTypes.forEach(
        selection =>
          (attendees[key][selection.templateAttendeeTypeId ?? selection.id] =
            selection.activeAttendeesCount)
      )
    }
    return attendees
  }, [selectionTypes])

  const templateAttendeeTypes = selectionTypes.filter(at => at.templateAttendeeTypeId == null)

  const anySelectionsWithCost = selectionTypes.some(sel => sel.priceCents > 0)

  const validateFields = changedFields => {
    let validation = {}

    const hasTotalCapacity = isPresent(totalCapacity)
    let totalAttendeeCount = registerForSeparateTimesEnabled ? 0 : signup.activeAttendeesCount

    const capacityLessThanSelectionCapacity =
      hasTotalCapacity &&
      selectionTypes.some(selectionType => {
        return Number(totalCapacity) < Number(selectionType.maximumCapacity)
      })
    if (capacityLessThanSelectionCapacity) {
      validation["total"] = "Capacity must be greater than or equal to selection capacity"
    }

    if (registerForSeparateTimesEnabled && hasTotalCapacity) {
      signup.eventTimes.forEach(signupTime => {
        const signupTimeCapacity =
          activeAttendees[`activeAttendeesFor${signupTime.id}`]?.activeAttendeesCount
        if (signupTimeCapacity && Number(totalCapacity) < Number(signupTimeCapacity)) {
          validation["total"] = "Capacity must be greater than or equal to attending"
        }
      })
    }

    if (
      !registerForSeparateTimesEnabled &&
      hasTotalCapacity &&
      Number(totalCapacity) < Number(totalAttendeeCount)
    ) {
      validation["total"] = "Capacity must be greater than or equal to attending"
    }

    Object.values(changedFields).map(atf => {
      const attendeeType = selectionTypes.find(at => at.id === atf.id)
      const attendeeTypeMaxCapacity = atf.maximum_capacity
      const hasMaximumCapacity = isPresent(attendeeTypeMaxCapacity)
      const attendeeTypeAttendeeCount = attendeeType.activeAttendeesCount

      if (
        hasMaximumCapacity &&
        hasTotalCapacity &&
        Number(attendeeTypeMaxCapacity) > Number(totalCapacity)
      ) {
        validation[atf.id] = "Capacity must be less than or equal to signup capacity"
      } else if (
        hasMaximumCapacity &&
        Number(attendeeTypeMaxCapacity) < Number(attendeeTypeAttendeeCount)
      ) {
        validation[atf.id] = "Capacity must be greater than or equal to attending"
      } else {
        delete validation[atf.id]
      }
    })

    return validation
  }

  const handleWaitlistChange = attrs => {
    status.setSaving()

    return onWaitlistSave(attrs)
      .then(() => status.setSuccess())
      .catch(({ errors }) => status.setError(errors[0]))
  }

  const handleCapacityChange = async attrs => {
    if (attrs.id) {
      if (
        !capacityChanges[attrs.id] ||
        attrs.maximum_capacity !=
          selectionTypes.find(st => Number(st.id) == Number(attrs.id)).maximumCapacity
      ) {
        setCapacityChanges({
          ...capacityChanges,
          [attrs.id]: attrs
        })
      } else {
        setCapacityChanges({ ...delete capacityChanges[attrs.id] })
      }
      setAttendeeTypeAttrs({
        ...attendeeTypeAttrs,
        [attrs.id]: attrs
      })
    } else {
      setTotalCapacity(attrs.maximum_capacity)
    }
  }

  const handleCapacitySave = () => {
    const validation = validateFields(capacityChanges)

    if (isPresent(validation)) {
      return setValidation(validation)
    }

    setValidation({})

    status.setSaving()

    return onCapacitySave({ totalCapacity, changes: capacityChanges })
      .then(_ => {
        setEditCapacity(false)
        status.setSuccess()
        setCapacityChanges({})
      })
      .catch(({ errors }) => {
        status.setError(errors[0].detail)
      })
  }

  const handleNewClick = () => {
    const path = `/signups/${signup.id}/attendee_types/new`

    navigate(path)
  }

  const handleRowClick = (row, e) => {
    if (e.target.localName !== "td") {
      return
    }

    const path = `/attendee_types/${row.id}/edit`

    navigate(path)
  }

  const handleColumnSelector = (updatedColumns, _column) => {
    setSelectedColumns(updatedColumns)
  }

  const handleOptionsUpdate = useOptionsUpdate({
    signup,
    status,
    setRegisterForSeparateTimesEnabled,
    setSignupTimes,
    signupTimes
  })

  const renderRestrictions = st => {
    return (
      <>
        {st.genderRestrictions && <div>{st.genderRestrictions}</div>}
        {st.ageRestrictions && <div>{st.ageRestrictions}</div>}
        {st.gradeRestrictions && <div>{st.gradeRestrictions}</div>}
      </>
    )
  }

  const renderAttendeeTypeName = at => {
    return (
      <>
        {at.name}
        {!at.publiclyAvailable && (
          <span className="m-1 c-warning-icon" data-tooltip="Not shown on Church Center">
            <Icon symbol="general#hide-eye" />
          </span>
        )}
      </>
    )
  }

  const renderCost = at => {
    return !at.priceCents || at.priceCents == 0 ? (
      <span className="ws-nw">
        {at.currencySymbol}
        {at.priceFormatted} {at.currency}
      </span>
    ) : (
      <>
        <span className="ws-nw">
          {at.currencySymbol}
          {at.priceFormatted} {at.currency}
        </span>
        {at.allowDeposit && (
          <div className="c-secondary font-size-5">
            {at.currencySymbol}
            {at.minimumDepositFormatted} minimum deposit
          </div>
        )}
      </>
    )
  }

  const datelessColumn = () => {
    const key = "activeAttendeesForDateless"

    templateAttendeeTypes.forEach(selection => {
      selection[key] = activeAttendees[key][selection.templateAttendeeTypeId ?? selection.id] ?? 0

      selection[key] = selection.maximumCapacity
        ? `${selection[key]} / ${selection.maximumCapacity}`
        : selection[key]
    })

    return {
      cell: key,
      label: "Registrations",
      isSelectable: false,
      isSelected: true,
      style: { minWidth: 120 }
    }
  }

  let columns = [
    { cell: "name", label: "Name", style: { minWidth: 140 } },
    anySelectionsWithCost ? { cell: "cost", label: "Cost" } : null,
    { cell: "restrictions", label: "Restrictions" }
  ].filter(n => n)

  const signupTimeColumns = []

  signupTimes.every(signupTime => {
    const key = `activeAttendeesFor${signupTime.id}`
    const selectedColumn = selectedColumns.find(sc => sc.cell === key)

    templateAttendeeTypes.forEach(selection => {
      selection[key] = activeAttendees[key][selection.templateAttendeeTypeId ?? selection.id] ?? 0

      selection[key] = selection.maximumCapacity
        ? `${selection[key]} / ${selection.maximumCapacity}`
        : selection[key]
    })

    signupTimeColumns.push({
      cell: key,
      label: registerForSeparateTimesEnabled ? `${signupTime.contextualDateTime}` : "Registrations",
      isSelectable: registerForSeparateTimesEnabled,
      isSelected: selectedColumn ? selectedColumn.isSelected : true,
      style: { minWidth: 120 }
    })
    return registerForSeparateTimesEnabled
  })

  signupTimeColumns.some(Boolean)
    ? columns.push(...signupTimeColumns)
    : columns.push(datelessColumn())

  columns.push({
    cell: "capacity",
    label: (
      <div>
        {editCapacity && (
          <>
            <button onClick={() => setEditCapacity(false)} className="btn-ghost mr-1">
              Cancel
            </button>
            <button onClick={handleCapacitySave} className="btn-solid interaction">
              Save
            </button>
          </>
        )}
        {!editCapacity && (
          <button onClick={() => setEditCapacity(true)} className="btn-outline">
            Edit capacities
          </button>
        )}
      </div>
    )
  })

  const waitlistDisabled = detailedWaitlistFlipperEnabled
    ? !unifiedSignupsEnabled
    : signup.registrationType != "simple"

  const waitlistDisabledReason = detailedWaitlistFlipperEnabled
    ? "Waitlist is not available for legacy signups"
    : "Waitlist is only available for simple signups"

  const rows = templateAttendeeTypes.map(st => ({
    ...st,
    restrictions: renderRestrictions(st),
    isClickDisabled: editCapacity,
    name: renderAttendeeTypeName(st),
    cost: renderCost(st),

    capacity: editCapacity ? (
      <div>
        <CapacityField
          selectionType={st}
          signupMaxCapacity={signup.maximumCapacity}
          valid={validation[st.id]}
          onChange={handleCapacityChange}
        />
      </div>
    ) : (
      <WaitlistField
        disabled={waitlistDisabled}
        disabledReason={waitlistDisabled ? waitlistDisabledReason : null}
        selectionType={st}
        signup={signup}
        onChange={handleWaitlistChange}
      />
    )
  }))

  rows.push({
    id: "footer",
    // eslint-disable-next-line react/display-name
    render: (_row, columns) => {
      const selectedColumns = columns.filter(c => c.isSelected).map(c => c.cell)

      return (
        <>
          <td colSpan={anySelectionsWithCost ? 4 : 3}>
            <>
              <div className="d-f ai-c">
                <strong className="mr-1">
                  {signup?.registerForSeparateTimes ? "Total per Date/Time" : "Total"}
                </strong>
                <HelperShelfTrigger id="simple_attendee_counts_explanation" className="p-r t-1p" />
              </div>
              <HelperShelfMessage id="simple_attendee_counts_explanation">
                These counts include any registrations that are currently in progress on Church
                Center. If the registrant doesn&apos;t complete their registration within 15
                minutes, their selections will be released to other attendees.
              </HelperShelfMessage>
            </>
          </td>
          {registerForSeparateTimesEnabled ? (
            signupTimes.map(
              signupTime =>
                selectedColumns.includes(`activeAttendeesFor${signupTime.id}`) && (
                  <td key={signupTime.id} style={{ fontWeight: "bold" }}>
                    {signup.maximumCapacity
                      ? `${
                          activeAttendees[`activeAttendeesFor${signupTime.id}`].activeAttendeesCount
                        } / ${signup.maximumCapacity}`
                      : activeAttendees[`activeAttendeesFor${signupTime.id}`].activeAttendeesCount}
                  </td>
                )
            )
          ) : (
            <td style={{ fontWeight: "bold" }}>
              {signup.maximumCapacity
                ? `${signup.activeAttendeesCount} / ${signup.maximumCapacity}`
                : signup.activeAttendeesCount}
            </td>
          )}
          <td>
            {editCapacity && (
              <CapacityField
                onChange={handleCapacityChange}
                signupMaxCapacity={signup.maximumCapacity}
                valid={validation["total"]}
              />
            )}
          </td>
        </>
      )
    }
  })

  return (
    <>
      {isLoading && null}
      {!isLoading && isBlank(selectionTypes) && (
        <BlankState
          imageURL="/static/blank-states/registrations-assignments.svg"
          onButtonClick={handleNewClick}
          buttonLabel="New selection"
          placeholderText="You haven't created any selections for this signup yet"
          helpLinkText="What are selections?"
          helpLinkURL="https://pcoregistrations.zendesk.com/hc/en-us/articles/204733970-Attendee-Types"
        />
      )}
      {!isLoading && isPresent(selectionTypes) && (
        <Table
          heading={{
            headerText: pluralize("selection", "selections", templateAttendeeTypes.length)
          }}
          headingChildren={
            <>
              <SelectionTypesOptionsWrapper
                signup={signup}
                handleOptionsUpdate={handleOptionsUpdate}
                status={status}
                signupTimes={signupTimes}
                registerForSeparateTimesEnabled={registerForSeparateTimesEnabled}
                canChangeRegisterForSeparateTimes={signup?.canChangeRegisterForSeparateTimes}
              />
              <button onClick={handleNewClick} className="btn-solid interaction">
                New selection
              </button>
            </>
          }
          columns={columns}
          rows={rows}
          onDragEnd={onDragEnd}
          onRowClick={handleRowClick}
          onColumnSelector={handleColumnSelector}
          draggableType="handle"
          status={status.get()}
          tabIndex={0}
        />
      )}
    </>
  )
}

SelectionTypesList.propTypes = {
  signup: shape({
    isSimple: bool,
    attendeesCount: number,
    maximumCapacity: number,
    waitlistAttendeesCount: number,
    maximumRegistrationAttendees: number
  }),
  selectionTypes: array,
  onCapacitySave: func,
  onWaitlistSave: func,
  onDragEnd: func,
  signupTimes: arrayOf(
    shape({
      id: number
    })
  ),
  isLoading: bool
}

export default SelectionTypesList
